import React, { useEffect, useRef } from 'react'
import { useLocale } from 'antd/es/locale'

import { Dialog, DialogContent, DialogTitle } from 'src/components/ui/dialog'
import { cn } from 'src/lib/utils'

import { MODAL_EXIT_MS } from './constants'
import type { ButtonProps, ModalProps } from '../antd-props'
import { Button } from '../Button.shadcn'
import { omitTransient } from '../overlay/placement'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Modal` seam. `./index.tsx` is the
 * dispatcher that chooses between this and antd's own Modal; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * The external API is antd's own `ModalProps`, so the 88 files that import
 * `Modal` from `src/antd` — and the house wrapper in
 * `src/storybook/components/Modal`, which is `styled(Modal)` — switch with no
 * edit at all.
 *
 * ---------------------------------------------------------------------------
 * THE CONTAINER DECISION. Body portal, same as `../Tooltip.shadcn.tsx` and for
 * the same reason; read the long version there. antd's Modal portals to body
 * too (`getContainer` defaults to `document.body`), so this is parity, not a
 * choice made for shadcn's convenience. `getContainer` is honoured when a call
 * site passes an ELEMENT, or a function returning one; every other form falls
 * back to the body portal. No call site passes any of them today.
 *
 * ---------------------------------------------------------------------------
 * FOCUS AND DISMISSAL — accessibility properties, not styling
 *
 *  • FOCUS TRAP. Radix's Dialog traps focus while `modal` is true. antd traps
 *    it regardless of whether a mask is drawn, so `modal` is NOT wired to
 *    `mask` here (the reference branch wired them together, which silently
 *    drops the trap for a maskless modal). `mask` gates the dim OVERLAY only.
 *  • ESCAPE. antd closes on Escape unless `keyboard={false}`; Radix's
 *    `onEscapeKeyDown` is prevented in exactly that case.
 *  • MASK CLICK. antd closes unless `maskClosable={false}`; the two Radix
 *    outside-dismiss events are prevented in exactly that case.
 *  • FOCUS RESTORE. Radix's modal Dialog restores focus to its own
 *    `Dialog.Trigger`, and this seam has none — every call site drives `open`
 *    itself — so out of the box focus would land on `<body>`. The seam
 *    captures the previously focused element and restores it, which is what
 *    antd's rc-dialog does.
 *
 *  KNOWN DIVERGENCE, reported rather than hidden: on open, antd focuses the
 *  dialog PANEL, Radix focuses the first tabbable control inside it. A form
 *  modal therefore lands with its first field focused in shadcn mode. This is
 *  Radix's default and is the more common expectation, but it IS a change.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS NOT PORTED, named rather than pretended:
 *  • `forceRender` (2 call sites) — Radix unmounts a closed dialog, so a
 *    `useForm` instance no longer stays connected while closed and antd's
 *    "Instance created by `useForm` is not connected to any Form element"
 *    warning returns at those two sites in shadcn mode. Nothing else changes.
 *  • `destroyOnClose` (80 call sites) is a no-op for the opposite reason:
 *    Radix always destroys, which is what every one of those sites asked for.
 *  • `centered` (0 call sites) is a no-op: this panel is ALWAYS centred, where
 *    antd's default sits 100px from the top of a scrolling page. That is a
 *    visual change for every modal, and the reason `../../components/ui/
 *    dialog.tsx` gives the panel a max-height and its own scrollbar — a
 *    centred fixed box taller than the viewport would otherwise be unreachable.
 *  • `getContainer={false}` (antd renders the panel in place, with no portal
 *    at all) and `getContainer='<selector>'` (antd resolves the string) — 0
 *    call sites each, so neither is implemented rather than carried untested;
 *    both fall back to the body portal. Radix's `container` takes an element,
 *    so the string form would otherwise be handed straight through and throw.
 *  • `closeIcon`, `wrapClassName`, `modalRender`, `focusTriggerAfterClose`,
 *    `mousePosition` — no call site sets any of them.
 * ------------------------------------------------------------------------- */

export type { ModalProps }

const Modal = ({
  open,
  visible,
  onCancel,
  onOk,
  title,
  footer,
  okText,
  cancelText,
  okType,
  okButtonProps,
  cancelButtonProps,
  confirmLoading,
  width,
  closable = true,
  maskClosable = true,
  keyboard = true,
  mask = true,
  styles,
  bodyStyle,
  className,
  style,
  zIndex,
  afterClose,
  getContainer,
  // accepted, then dropped so they never reach the DOM — see the header
  closeIcon: _closeIcon,
  centered: _centered,
  destroyOnClose: _destroyOnClose,
  forceRender: _forceRender,
  wrapClassName: _wrapClassName,
  modalRender: _modalRender,
  focusTriggerAfterClose: _focusTriggerAfterClose,
  mousePosition: _mousePosition,
  children,
  ...rest
}: ModalProps): JSX.Element => {
  const isOpen = open ?? visible ?? false
  const [locale] = useLocale('Modal')

  // FOCUS RESTORE, and why it is not free. Radix's modal Dialog restores focus
  // to its own `Dialog.Trigger` — and this seam has none, because every call
  // site drives `open` itself. Left alone that means focus lands on `<body>`
  // when a modal closes, where antd's rc-dialog returns it to whatever was
  // focused before the modal opened. So the seam captures that element on the
  // closed→open edge and puts focus back itself.
  //
  // Read during render rather than in an effect on purpose: by the time any
  // effect of THIS component runs, the Radix content below has already mounted
  // and moved focus into the panel, so the answer would already be wrong.
  // Reading `document.activeElement` is a pure observation, and the write is
  // the standard previous-value ref pattern.
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  const wasOpenForFocus = useRef(false)
  if (isOpen && !wasOpenForFocus.current) {
    restoreFocusRef.current = document.activeElement as HTMLElement | null
  }
  wasOpenForFocus.current = isOpen

  // Fire `afterClose` once the close transition has run (open true → false).
  //
  // The callback is held in a REF and the effect keys on `isOpen` alone, which
  // is the whole point: every call site passes an inline arrow, so a parent
  // re-render inside the exit window hands over a new identity. With that
  // identity in the dep list the effect would re-run, its cleanup would clear
  // the pending timer, and the re-run would see `prevOpen.current === false`
  // and schedule nothing — dropping the call. `asyncAlert` destroys the
  // modal's container and root from here, so a dropped call is a leak.
  const afterCloseRef = useRef(afterClose)
  afterCloseRef.current = afterClose
  const prevOpen = useRef(isOpen)
  useEffect(() => {
    if (prevOpen.current && !isOpen) {
      prevOpen.current = isOpen
      const timer = setTimeout(() => afterCloseRef.current?.(), MODAL_EXIT_MS)
      return () => clearTimeout(timer)
    }
    prevOpen.current = isOpen
    return undefined
  }, [isOpen])

  const handleOpenChange = (next: boolean) => {
    // Radix reports every dismissal here — Escape, mask click, the close
    // button. antd routes all three to `onCancel`, so this does too. The
    // event antd would pass is not available on every path; call sites take a
    // `React.MouseEvent` they never read.
    if (!next) onCancel?.(undefined as never)
  }

  const bodyStyles: React.CSSProperties = {
    ...((styles?.body as React.CSSProperties) ?? {}),
    ...((bodyStyle as React.CSSProperties) ?? {}),
  }

  const renderFooter = () => {
    if (footer === null || footer === false) return null

    const inner =
      footer === undefined ? (
        // antd's default footer, in antd's own order and with antd's own
        // prop precedence (the *ButtonProps spread wins over the defaults).
        <>
          <Button
            htmlType="button"
            onClick={(event) => onCancel?.(event as never)}
            {...(cancelButtonProps as ButtonProps)}
          >
            {cancelText ?? locale.cancelText}
          </Button>
          <Button
            htmlType="button"
            type="primary"
            danger={okType === 'danger'}
            loading={confirmLoading}
            onClick={(event) => onOk?.(event as never)}
            {...(okButtonProps as ButtonProps)}
          >
            {okText ?? locale.okText}
          </Button>
        </>
      ) : (
        (footer as React.ReactNode)
      )

    return (
      <div
        data-slot="modal-footer"
        className="mt-6 flex shrink-0 flex-row justify-end gap-2"
        style={styles?.footer as React.CSSProperties}
      >
        {inner}
      </div>
    )
  }

  // An element, or a function returning one. Everything else antd accepts —
  // `false`, a string selector — is left unported (see the header) and falls
  // back to the body portal rather than reaching Radix, which takes an element
  // and would throw on a string.
  const resolvedContainer =
    typeof getContainer === 'function' ? getContainer() : getContainer
  const container =
    resolvedContainer instanceof HTMLElement ? resolvedContainer : undefined

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal>
      <DialogContent
        showCloseButton={closable !== false}
        mask={mask}
        maskStyle={styles?.mask as React.CSSProperties}
        container={container}
        // antd hard-codes this one too (`antd/es/modal/Modal.js`), so it is
        // parity rather than an un-translated string. It must NOT reuse
        // `locale.cancelText`: the footer already has a "Cancel" button, and a
        // second control with the same accessible name is ambiguous to a
        // screen reader and to `getByRole`.
        closeLabel="Close"
        className={cn('gap-0', className)}
        style={{
          ...(width != null ? { width, maxWidth: 'calc(100% - 2rem)' } : {}),
          ...(zIndex != null ? { zIndex } : {}),
          ...((styles?.content as React.CSSProperties) ?? {}),
          ...((style as React.CSSProperties) ?? {}),
        }}
        onPointerDownOutside={(event) => {
          if (!maskClosable) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (!maskClosable) event.preventDefault()
        }}
        onEscapeKeyDown={(event) => {
          if (!keyboard) event.preventDefault()
        }}
        onCloseAutoFocus={(event) => {
          // Pre-empt Radix's own handler (it would focus an absent trigger).
          event.preventDefault()
          restoreFocusRef.current?.focus()
        }}
        {...omitTransient(rest as Record<string, unknown>)}
      >
        {title != null ? (
          <div
            data-slot="modal-header"
            className="mb-4 shrink-0 border-b pb-3"
            style={styles?.header as React.CSSProperties}
          >
            <DialogTitle data-slot="modal-title">{title}</DialogTitle>
          </div>
        ) : (
          // Radix requires an accessible name even when antd's Modal renders
          // no visible header.
          <DialogTitle className="sr-only">Dialog</DialogTitle>
        )}
        <div
          data-slot="modal-body"
          className="min-h-0 flex-1"
          style={bodyStyles}
        >
          {children}
        </div>
        {renderFooter()}
      </DialogContent>
    </Dialog>
  )
}

export { Modal }
